import { useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { User, Mail, Lock, Sparkles, ArrowRight } from "lucide-react";
import SEO from "../components/SEO";
import { supabase } from "../supabaseClient";

export default function Signup() {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({ name: "", email: "", password: "" });
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.email || !formData.password || !formData.name) return;

        setIsLoading(true);
        setErrorMsg("");

        try {
            const { data, error } = await supabase.auth.signUp({
                email: formData.email,
                password: formData.password,
                options: {
                    data: {
                        full_name: formData.name,
                    }
                }
            });

            if (error) throw error;

            console.log("Signup success:", data);
            
            if (data.session) {
                // If auto-confirm is enabled, redirect directly to dashboard
                setIsSuccess(true);
                setTimeout(() => {
                    navigate("/dashboard");
                }, 1500);
            } else {
                // If email confirmation is required
                setIsSuccess(true);
            }
        } catch (error) {
            console.error("Signup error:", error.message);
            setErrorMsg(error.message || "An error occurred during signup");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="flex justify-center items-center py-20 px-4">
            <SEO title="Create Your Account" description="Join blinqbooks today and start automating your business with AI." />

            <div className={`bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl rounded-[2rem] p-8 md:p-12 w-full max-w-lg transition-all duration-500 relative overflow-hidden ${isLoading ? 'scale-[1.02]' : ''}`}>
                {/* Decorative background */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-accent/5 rounded-full blur-3xl pointer-events-none"></div>

                <div className="text-center mb-10 relative z-10">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 text-accent text-[10px] font-bold uppercase tracking-widest mb-4">
                        <Sparkles className="w-3 h-3" /> Start Your 14-Day Free Trial
                    </div>
                    <h1 className="text-4xl font-black text-primary dark:text-white mb-3">
                        {isSuccess ? 'Check your email!' : 'Create Your blinqbooks Account'}
                    </h1>
                    <p className="text-secondary dark:text-gray-400">Join 50,000+ businesses running on blinqbooks AI.</p>
                </div>

                {errorMsg && (
                    <div className="p-3 mb-6 rounded-lg bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400 text-sm text-center font-medium relative z-10">
                        {errorMsg}
                    </div>
                )}

                {!isSuccess ? (
                    <form onSubmit={handleSubmit} className="space-y-5 relative z-10">
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Full Name</label>
                            <div className="relative">
                                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type="text"
                                    name="name"
                                    placeholder="John Doe"
                                    onChange={handleChange}
                                    required
                                    className="w-full pl-12 pr-4 py-4 rounded-xl bg-slate-500/5 dark:bg-white/5 border border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-accent outline-none transition-all"
                                />
                            </div>
                        </div>

                        <div className="space-y-1">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Work Email</label>
                            <div className="relative">
                                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type="email"
                                    name="email"
                                    placeholder="name@company.com"
                                    onChange={handleChange}
                                    required
                                    className="w-full pl-12 pr-4 py-4 rounded-xl bg-slate-500/5 dark:bg-white/5 border border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-accent outline-none transition-all"
                                />
                            </div>
                        </div>

                        <div className="space-y-1">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Password</label>
                            <div className="relative">
                                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type="password"
                                    name="password"
                                    placeholder="••••••••"
                                    onChange={handleChange}
                                    required
                                    className="w-full pl-12 pr-4 py-4 rounded-xl bg-slate-500/5 dark:bg-white/5 border border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-accent outline-none transition-all"
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={isLoading}
                            className={`w-full py-4 mt-4 font-bold text-white rounded-full transition-all duration-300 flex items-center justify-center gap-2 group ${isLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-accent hover:bg-accent-hover transform hover:-translate-y-1 active:scale-95'}`}
                        >
                            {isLoading ? (
                                <svg className="animate-spin w-5 h-5 text-white" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            ) : (
                                <>Get Started for Free <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" /></>
                            )}
                        </button>

                        <div className="text-center mt-8">
                            <p className="text-sm text-secondary dark:text-gray-400">
                                Already have an account?{' '}
                                <Link to="/login" className="text-accent font-bold hover:underline">Log in</Link>
                            </p>
                        </div>
                    </form>
                ) : (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="text-center py-10"
                    >
                        <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                            <Mail className="w-10 h-10" />
                        </div>
                        <p className="text-secondary dark:text-gray-300 mb-8">
                            We've sent a magic link to <span className="font-bold text-primary dark:text-white">{formData.email}</span>. Click the link to verify your account.
                        </p>
                        <button onClick={() => setIsSuccess(false)} className="text-accent text-sm font-bold hover:underline">
                            Wrong email? Try again
                        </button>
                    </motion.div>
                )}
            </div>
        </div>
    );
}
